import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import api, { paymentsAPI, subscriptionAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { extractErrorMessage } from '@/lib/errors';
import { ArrowLeft, Check, CreditCard, Loader2, Crown } from 'lucide-react';

const ROLE_ICONS = {
  farmer: '🌾',
  manufacturer: '🏭',
  distributor: '🚚',
  shopkeeper: '🏪',
};

const TIER_ORDER = ['free', 'basic'];

export default function PlansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const userTypeParam = searchParams.get('userType');
  const isNewUser = searchParams.get('isNewUser') === 'true';
  const userType = userTypeParam || user?.user_type || 'farmer';

  const [plansByTier, setPlansByTier] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState(null);
  const [error, setError] = useState(null);
  // Startbutton: the USD plan price shown and charged in naira.
  const [provider, setProvider] = useState(null);
  const [quotes, setQuotes] = useState({});
  // Monthly (30 days) or yearly (12 months; the first year is 10 months' price).
  const [billing, setBilling] = useState('month');

  const roleLabel = t(`common.role.${userType}`, userType);
  const roleIcon = ROLE_ICONS[userType] || '👤';

  const naira = (amount) => `₦${Number(amount).toLocaleString()}`;
  const quoteFor = (tier) => (billing === 'year' ? quotes[tier]?.year : quotes[tier]);

  const formatPrice = (value, tier) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return t('plans.priceFree');
    if (provider === 'startbutton') {
      const amount = quoteFor(tier)?.amount_ngn;
      return amount ? naira(amount) : '…';
    }
    return `$${n.toFixed(2)}`;
  };
  const yearOffer = Object.values(quotes).some((q) => q?.year?.offer);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [plansResp, subResp] = await Promise.all([
          subscriptionAPI.getPlans(userType),
          user?.id ? subscriptionAPI.getUserSubscription(user.id) : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;
        const tiers = plansResp.data?.[userType] || {};
        setPlansByTier(tiers);
        setCurrentSubscription(subResp?.data?.subscription || null);
        const configResp = await paymentsAPI.config().catch(() => ({ data: {} }));
        if (cancelled) return;
        setProvider(configResp.data?.provider || null);
        if (configResp.data?.provider === 'startbutton') {
          const paid = Object.keys(tiers).filter((tier) => Number(tiers[tier]?.price) > 0);
          const found = await Promise.all(paid.map((tier) => paymentsAPI.quote(tier)
            .then((r) => [tier, r.data]).catch(() => [tier, null])));
          if (!cancelled) setQuotes(Object.fromEntries(found));
        }
      } catch (e) {
        if (cancelled) return;
        setError(extractErrorMessage(e, t('plans.errorDefault')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [userType, user?.id, t]);

  const orderedTiers = useMemo(() => {
    if (!plansByTier) return [];
    return TIER_ORDER.filter((tier) => plansByTier[tier]).map((tier) => ({ tier, ...plansByTier[tier] }));
  }, [plansByTier]);

  const currentTier = currentSubscription?.plan_tier || user?.subscription_tier || null;

  async function handleFreeTier(plan) {
    if (currentTier === 'free') {
      toast({ title: t('plans.toast.alreadyFree') });
      return;
    }
    setBusyTier('free');
    try {
      await subscriptionAPI.subscribe({ user_id: user.id, user_type: userType, plan_tier: 'free' });
      updateUser?.({ ...user, subscription_tier: 'free', subscription_status: 'active' });
      toast({ title: t('plans.toast.onPlan', { name: plan.name }) });
      navigate(isNewUser ? '/dashboard' : '/dashboard');
    } catch (e) {
      toast({
        title: t('plans.toast.freePlanFailedTitle'),
        description: extractErrorMessage(e, t('plans.toast.freePlanFailedDesc')),
        variant: 'destructive',
      });
    } finally {
      setBusyTier(null);
    }
  }

  async function handlePaidTier(tier) {
    // Startbutton payments add to the time already paid, so paying for the
    // current plan is allowed there (e.g. a monthly user buying the year).
    if (provider !== 'startbutton' && currentTier === tier && currentSubscription?.status === 'active') {
      toast({ title: t('plans.toast.alreadyOn') });
      return;
    }
    setBusyTier(tier);
    if (provider === 'startbutton') {
      try {
        // Startbutton's payment page, then back to /payments/return.
        const resp = await paymentsAPI.checkout(tier, billing);
        window.location.href = resp.data.checkout_url;
      } catch (e) {
        toast({
          title: t('plans.toast.checkoutFailedTitle'),
          description: extractErrorMessage(e, t('plans.toast.checkoutFailedDesc')),
          variant: 'destructive',
        });
        setBusyTier(null);
      }
      return;
    }
    try {
      const origin = window.location.origin;
      const resp = await api.post('/stripe/checkout-session', {
        // user_id intentionally omitted — the backend takes the
        // subscriber identity from the auth token now (prior versions
        // trusted this field and let anyone open a session for anyone).
        user_type: userType,
        plan_tier: tier,
        success_url: `${origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}&isNewUser=${isNewUser}`,
        cancel_url: `${origin}/subscription/cancel`,
      });
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        throw new Error('Stripe did not return a checkout URL');
      }
    } catch (e) {
      toast({
        title: t('plans.toast.checkoutFailedTitle'),
        description: extractErrorMessage(e, t('plans.toast.checkoutFailedDesc')),
        variant: 'destructive',
      });
      setBusyTier(null);
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-center">
          <p className="font-semibold text-destructive mb-2">{t('plans.errorTitle')}</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>{t('plans.retry')}</Button>
        </div>
      </div>
    );
  }

  const renderCTA = (isBusy, isCurrent, isPaid, canExtend) => {
    if (isBusy) {
      return (
        <>
          <Loader2 className="w-4 h-4 animate-spin mr-2 shrink-0" />
          <span className="truncate">
            {isPaid ? t(provider === 'startbutton' ? 'plans.openingPayment' : 'plans.openingStripe') : t('plans.activating')}
          </span>
        </>
      );
    }
    if (isCurrent && !canExtend) return <span className="truncate">{t('plans.currentPlan')}</span>;
    if (isPaid) {
      let label = 'plans.subscribeStripe';
      if (provider === 'startbutton') label = isCurrent ? 'plans.addTime' : 'plans.payNow';
      return (
        <>
          <CreditCard className="w-4 h-4 mr-2 shrink-0" />
          <span className="truncate">{t(label)}</span>
        </>
      );
    }
    return <span className="truncate">{t('plans.startFree')}</span>;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {!isNewUser && (
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="plans-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">
            {isNewUser ? t('plans.welcome', { role: roleLabel }) : t('plans.title', { role: roleLabel })}
          </h1>
          <p className="text-muted-foreground">
            {isNewUser ? t('plans.subtitleNew') : t('plans.subtitleManage')}
          </p>
        </div>
      </div>

      {/* Current subscription banner */}
      {currentSubscription?.status === 'active' && currentTier && currentTier !== 'free' && (
        <div className="bg-farm-primary/10 border border-farm-primary/20 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Crown className="w-6 h-6 text-farm-primary" />
          <div>
            <p className="font-semibold text-farm-primary">
              {t('plans.banner.current', { name: currentSubscription.plan_name || `${roleLabel} ${currentTier}` })}
            </p>
            <p className="text-sm text-muted-foreground">
              {currentSubscription.payment_method === 'startbutton'
                ? t('plans.banner.paidUntil', {
                  date: new Date(currentSubscription.end_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
                })
                : currentSubscription.payment_method === 'stripe'
                  ? t('plans.banner.stripe')
                  : t('plans.banner.free')}
            </p>
          </div>
        </div>
      )}

      {provider === 'startbutton' && Object.keys(quotes).length > 0 && (
        <div className="flex justify-center mb-4">
          <div className="inline-flex rounded-full bg-gray-100 p-1" role="group" data-testid="billing-switch">
            {['month', 'year'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setBilling(option)}
                aria-pressed={billing === option}
                data-testid={`billing-${option}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  billing === option ? 'bg-white shadow text-farm-primary' : 'text-gray-600'}`}
              >
                {t(option === 'year' ? 'plans.billing.year' : 'plans.billing.month')}
                {option === 'year' && yearOffer && (
                  <span className="ml-2 text-xs bg-farm-primary text-white px-2 py-0.5 rounded-full">
                    {t('plans.billing.twoMonthsFree')}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {orderedTiers.map(({ tier, ...plan }) => {
          const isCurrent = currentTier === tier && (
            tier === 'free' || currentSubscription?.status === 'active'
          );
          const isPaid = Number(plan.price) > 0;
          const isBusy = busyTier === tier;
          const canExtend = isPaid && provider === 'startbutton';
          const yearQuote = billing === 'year' && provider === 'startbutton' ? quotes[tier]?.year : null;

          return (
            <Card
              key={tier}
              className={`relative overflow-hidden transition-all ${isCurrent ? 'border-farm-primary border-2' : ''} ${isPaid ? 'shadow-lg' : ''}`}
              data-testid={`plan-card-${tier}`}
            >
              {isPaid && (
                <div className="absolute top-0 right-0 bg-farm-primary text-white px-3 py-1 text-xs font-semibold rounded-bl-lg">
                  {t('plans.recommended')}
                </div>
              )}

              <CardHeader>
                {/* Below ~300 px wide the title + price stack
                    vertically (title then price), so a long plan
                    name like "Shopkeeper Premium" never fights
                    for horizontal space with the "$29.99" tile.
                    At `sm` and up they sit side-by-side as before.
                    (Tester issue #4 / #33 — plan card narrows.) */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg leading-tight">
                      <span className="text-xl md:text-2xl">{roleIcon}</span>
                      <span className="min-w-0 break-words">
                        {plan.name || `${roleLabel} ${tier}`}
                      </span>
                    </CardTitle>
                    {isCurrent && (
                      <span className="inline-block mt-2 text-xs bg-farm-primary/20 text-farm-primary px-2 py-1 rounded-full">
                        {t('plans.currentPlan')}
                      </span>
                    )}
                  </div>
                  <div className="text-left sm:text-right shrink-0">
                    <p className="text-2xl md:text-3xl font-bold leading-none whitespace-nowrap">
                      {formatPrice(plan.price, tier)}
                    </p>
                    {isPaid && (
                      <p className="text-xs md:text-sm text-muted-foreground mt-1">
                        {t(provider === 'startbutton' ? (yearQuote ? 'plans.per12Months' : 'plans.per30Days') : 'plans.perMonth')}
                      </p>
                    )}
                    {yearQuote?.offer && (
                      <p className="text-xs mt-1" data-testid={`year-offer-${tier}`}>
                        <s className="text-muted-foreground mr-1">{naira(yearQuote.full_amount_ngn)}</s>
                        <span className="font-semibold text-farm-primary">{t('plans.yearOffer')}</span>
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3 mb-6 min-h-[140px]">
                  {(plan.features || []).map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-farm-success flex-shrink-0 mt-1" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.add_ons?.length > 0 && (
                  <div className="mb-4 text-xs text-muted-foreground border-t pt-3">
                    <p className="font-medium mb-1">{t('plans.addOns')}</p>
                    <ul className="space-y-1">
                      {plan.add_ons.map((a, i) => (
                        <li key={i} className="flex justify-between">
                          <span>{a.name}</span>
                          <span className="tabular-nums">${Number(a.price).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button
                  className={`w-full min-w-0 inline-flex items-center justify-center whitespace-normal text-center h-auto min-h-10 py-2 px-3 ${
                    isCurrent && !canExtend
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : isPaid
                      ? 'bg-farm-primary hover:bg-farm-primary-dark'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  disabled={(isCurrent && !canExtend) || busyTier !== null}
                  onClick={() => (isPaid ? handlePaidTier(tier) : handleFreeTier(plan))}
                  data-testid={`subscribe-${tier}-btn`}
                >
                  {renderCTA(isBusy, isCurrent, isPaid, canExtend)}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-muted-foreground text-center">
        {t(provider === 'startbutton' ? 'plans.footerStartbutton' : 'plans.footer')}
      </p>
    </div>
  );
}
